import { useDemItemImageUrl } from '../../hooks/useDemItemImageUrl';
import {
  catalogueDisplayPriceRows,
  cataloguePriceIsDerived,
  cataloguePriceRowShowsMoq,
  formatCatalogueInventory,
  formatCatalogueMoqLabel,
  formatCataloguePriceWithUom,
  type CatalogueItem,
  type CataloguePriceTier,
} from '../../lib/catalogueItems';

export type CatalogueCertReports = {
  test: 'ready' | 'expired' | 'missing';
  msds: 'ready' | 'expired' | 'missing';
};

interface CatalogueCardProps {
  item: CatalogueItem;
  /** Required unless `hideCommerce` is true. */
  priceTier?: CataloguePriceTier;
  /** Hide inventory + price block (Purchasing certificates catalogue). */
  hideCommerce?: boolean;
  /** TEST / MSDS presence badges (dark = ready, grey = missing). */
  certReports?: CatalogueCertReports | null;
  selected?: boolean;
  onSelect?: (item: CatalogueItem) => void;
}

export function CatalogueCard({
  item,
  priceTier = 'CATERING',
  hideCommerce = false,
  certReports = null,
  selected = false,
  onSelect,
}: CatalogueCardProps) {
  const imageUrl = useDemItemImageUrl(item.image_url);
  const secondary = item.chinese_description?.trim();
  const ipg = item.inventory_posting_group.trim();
  const priceRows = hideCommerce ? [] : catalogueDisplayPriceRows(item, priceTier);
  const pricePending = item.pricesPending;
  const priceDerived = hideCommerce ? false : cataloguePriceIsDerived(item, priceTier);
  const uom = item.uom.trim();
  const clickable = typeof onSelect === 'function';

  return (
    <article
      className={[
        'catalogue-card',
        item.outOfStock ? 'catalogue-card--oos' : '',
        item.is_blocked ? 'catalogue-card--blocked' : '',
        item.enrichPending ? 'catalogue-card--pending' : '',
        selected ? 'catalogue-card--selected' : '',
        clickable ? 'catalogue-card--clickable' : '',
        hideCommerce ? 'catalogue-card--certs' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? selected : undefined}
      onClick={clickable ? () => onSelect(item) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(item);
              }
            }
          : undefined
      }
    >
      <div className="catalogue-card__media">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="catalogue-card__image" loading="lazy" />
        ) : (
          <div className="catalogue-card__image-placeholder" aria-hidden>
            <span className="catalogue-card__image-mark">BTH</span>
          </div>
        )}
        {item.outOfStock ? (
          <div className="catalogue-card__oos-overlay" aria-hidden>
            <span>Out of stock</span>
          </div>
        ) : null}
        <span className="catalogue-card__code">{item.code}</span>
        {ipg ? <span className="catalogue-card__ipg-chip">{ipg}</span> : null}
        {item.is_blocked ? <span className="catalogue-card__blocked-chip">Blocked</span> : null}
      </div>

      <div className="catalogue-card__body">
        <h2 className="catalogue-card__title">{item.description || item.code}</h2>
        {secondary ? <p className="catalogue-card__secondary">{secondary}</p> : null}
        {item.packing ? (
          <p className="catalogue-card__packing" translate="no">
            {item.packing}
          </p>
        ) : null}

        {hideCommerce ? (
          <div className="catalogue-card__cert-reports" aria-label="Certificate reports">
            <span
              className={`catalogue-card__cert-badge catalogue-card__cert-badge--${certReports?.test ?? 'missing'}`}
              title={
                certReports?.test === 'ready'
                  ? 'TEST ready'
                  : certReports?.test === 'expired'
                    ? 'TEST expired'
                    : 'TEST missing'
              }
            >
              TEST
            </span>
            <span
              className={`catalogue-card__cert-badge catalogue-card__cert-badge--${certReports?.msds ?? 'missing'}`}
              title={
                certReports?.msds === 'ready'
                  ? 'MSDS ready'
                  : certReports?.msds === 'expired'
                    ? 'MSDS expired'
                    : 'MSDS missing'
              }
            >
              MSDS
            </span>
          </div>
        ) : (
          <div className="catalogue-card__facts">
            <div className="catalogue-card__fact catalogue-card__fact--inventory">
              <span className="catalogue-card__fact-label">Inventory</span>
              <span
                className={
                  item.outOfStock
                    ? 'catalogue-card__fact-value catalogue-card__fact-value--warn'
                    : 'catalogue-card__fact-value'
                }
              >
                {item.enrichPending
                  ? '…'
                  : formatCatalogueInventory(item.inventory, item.outOfStock, item.base_uom)}
              </span>
            </div>

            <div className="catalogue-card__fact catalogue-card__fact--price">
              <span className="catalogue-card__fact-label">
                {priceTier}
                {priceDerived ? ' est.' : ''}
              </span>
              <ul className="catalogue-card__price-rows">
                {pricePending ? (
                  <li className="catalogue-card__price-row">…</li>
                ) : priceRows.length === 0 ? (
                  <li className="catalogue-card__price-row catalogue-card__price-row--empty">—</li>
                ) : (
                  priceRows.map((tier) => {
                    const showMoq = cataloguePriceRowShowsMoq(tier, priceRows);
                    return (
                      <li
                        key={`${tier.minQty}-${tier.price}`}
                        className={`catalogue-card__price-row${showMoq ? '' : ' catalogue-card__price-row--solo'}`}
                      >
                        {showMoq ? (
                          <span className="catalogue-card__price-moq">
                            {formatCatalogueMoqLabel(tier.minQty)}
                          </span>
                        ) : null}
                        <span className="catalogue-card__price-value">
                          {formatCataloguePriceWithUom(tier.price, uom)}
                        </span>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
